import {firstValueFrom, identity, Observable, ReplaySubject, Subject, Subscription, take, timeout} from "rxjs";
import {SearchService, SearchSortField} from "./search.service";
import {EntityData} from "../entity/entity.data";
import {Injector} from "@angular/core";
import {LocalisableEntityData} from "../entity/localisable-entity.data";
import {AbstractSearchModel, FilterValueChangedEvent} from "./abstract-search.model";

/**
 * Provides a search model and its dependencies
 * @param modelType
 * @returns {any[]}
 */
export function provideSearchModel(modelType: any): any[] {
    return [
        modelType,
        {provide: SearchModel, useExisting: modelType},
        {provide: AbstractSearchModel, useExisting: modelType}
    ];
}


export class SearchResult {
    data: any[];
    totalCount: number;
    countable: boolean;

    constructor(data: any[], total: number, countable: boolean) {
        this.data = data;
        this.totalCount = total;
        this.countable = countable;
    }
}

/**
 * Base class for search entity models. A model represents the resource state, as well as provides operations
 * that can be performed on the resource.
 *
 * A default service implementation is provided for performing server side communication. More specific implementations
 * may be provided via the subclass constructor if required.
 *
 * Generic parameter T defines the type of a array of EntityData to be held by the model. In case a EntityData is not required, the any can be used.
 *
 * Created by Sergio Gomes
 */
export abstract class SearchModel<T extends EntityData<T>> extends AbstractSearchModel {

    /**
     * Define the total number of records returned from the last search
     */
    public total: number;

    public countable: boolean;

    /**
     * Define the data for the model returned from the last search.
     */
    public data: Array<T>;

    /**
     * Can be subscribed to get the search data when it is resolved
     * @type {ReplaySubject<any>}
     * @private
     */
    private _$data: Subject<Array<T>> = new ReplaySubject(1);
    public $data: Observable<Array<T>> = this._$data.asObservable();


    /**
     * Define the limit of records to fetch
     * @type {number}
     */
    public limit: number;

    /**
     * Define the start record for the search
     */
    public start: number;

    /**
     * Define if the model is pageable
     */
    public pageable: boolean = true;

    /**
     * Define the filter for the search
     * @type {{}}
     */
    private _internalFilter: any = {};

    public sort: SearchSortField[];

    protected service: SearchService;

    /**
     * Define the pagination data in simple object. i.e. {data: data, total: dataCount, start: start, limit: limit, sort: sort}
     */
    public dataView: any;

    public dataViewChange: Subject<any> = new Subject();

    private lastSearchSubscription: Subscription;

    /**
     * Constructs with the service which is used for all server side comms.
     *
     * This constructor must be replicated in subclasses to allow Angular dependency injection to provide the service.
     * Subclasses may also specify a more specialised service that extends EntityService. If so, then the subclass
     * must also override #getEntityService
     *
     * @param injector Injector
     * @param dataType EntityData subclass
     * @param resourceVersion Resource version
     */
    constructor(protected injector: Injector, private dataType?: typeof EntityData) {
        super();
        if (injector == null) {
            console.error("!!!! Injector not provided. This is most likely due to a missing constructor in " + this.constructor.toString());
        }
        this.createService();
        this.data = [];
    }

    private createService(): void {
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: SearchService.SEARCH_RESOURCE,
                    useFactory: () => this.getResourceName()
                },
                {
                    provide: SearchService.RESOURCE_VERSION,
                    useFactory: () => this.getResourceVersion()
                },
                this.getSearchService()
            ],
            parent: this.injector
        });
        this.service = injector.get(this.getSearchService());
    }

    public getNameSpace(): string {
        return this.service.getNameSpace();
    }

    public getResource(): string {
        return this.service.getResource();
    }

    /**
     * Retrieves the name of the model
     * @returns {string}
     */
    protected getModelName(): string {
        return this.constructor.name.replace(/^_/, "");
    }

    /**
     * Retrieves the kebab case name of the model which is in a format that matches the naming convention of
     * the server side resource
     * @returns {string} Name of this model
     */
    public getResourceName(): string {
        return this.getModelName()
            .replace(/Model$/, "")
            .replace(/Search$/, "")
            .replace(/\.?([A-Z])/g, (x, y) => "-" + y.toLowerCase())
            .replace(/^-/, "");
    }

    /**
     * Method to be override from the children in case the search results are not entity data
     * @returns {null}
     */
    getIdentifierField(): string {
        return null;
    }

    getIdentifier(item: T): any {
        let identifierField: string = this.getIdentifierField();
        if (!identifierField) {
            if (item instanceof EntityData) {
                identifierField = (<EntityData<any>>item).getIdentifierKey();
            }
        }
        if (!identifierField) {
            throw new Error("Identifier field not defined.");
        }
        return item[identifierField];
    }

    /**
     * Helper method to allow framework components to check for discontinued search result items.
     * The default behaviour is to return the value of a "discontinued" field on the item.
     * This method may be overridden by search model implementations, however for search results mapped to EntityData classes,
     * the preferred approach is to provide an appropriate getter on the EntityData class itself if required.
     * @param item A search result item.
     * @return true if the item is discontinued, false if not, or undefined if the item does not support discontinuation.
     */
    isDiscontinued(item: T): boolean {
        return item["discontinued"];
    }

    /**
     * Helper method to allow framework components to set search result items as discontinued.
     * The default behaviour is to set the value of a "discontinued" field on the item.
     * This method may be overridden by search model implementations, however for search results mapped to EntityData classes,
     * the preferred approach is to provide an appropriate setter on the EntityData class itself if required.
     * @param item A search result item.
     * @param discontinued
     */
    setDiscontinued(item: T, discontinued: boolean): void {
        item["discontinued"] = discontinued;
    }

    /**
     * Returns the service type expected by this model (defaults to a generic SearchService).
     * Extending models should override this if a specialised service is required.
     * @returns {EntityService}
     */
    protected getSearchService(): typeof SearchService {
        return SearchService;
    }

    searchRecords(filter: any, start: number, limit: number, sort: SearchSortField[]): Observable<SearchResult> {
        let result: Subject<SearchResult> = new ReplaySubject(1);

        this.service.search(filter, start, limit, sort).subscribe({
            next: (json: any) => {
                let data: any[];
                if (this.dataType) {
                    data = <T[]>EntityData.createArray(json.list, this.dataType);
                } else {
                    data = json.list;
                }
                result.next(new SearchResult(this.postProcessData(data), json.totalCount, json.countable));
                result.complete();
            }, error: (error: any) => {
                result.error(error);
                result.complete();
            }
        });
        return result;
    }

    protected postProcessData(data: any[]): any[] {
        return data;
    }

    private createSearch(): Observable<SearchResult> {
        return this.searchRecords(this.filter, this.pageable ? this.start : undefined, this.pageable ? this.limit : undefined, this.sort);
    }

    /**
     * We are subscribing to the observable to capture the data,
     * so we need to wrap the observable in a ReplaySubject so that our caller can also see the result.
     */
    private createSearchResultHandler(): Subject<any> {
        const s: Subject<any> = new ReplaySubject<any>();
        s.subscribe({
            next: (searchResult: SearchResult) => {
                this.data = searchResult.data;
                this.total = searchResult.totalCount;
                this.countable = searchResult.countable;

                this.dataView = {
                    data: this.data,
                    total: this.total,
                    start: this.start,
                    limit: this.limit,
                    sort: this.sort
                };
                this.dataViewChange.next(this.dataView);
                this._$data.next(this.data);
            }, error: (error: any) => {
            }
        });
        return s;
    }

    /**
     * Performs the search "asynchronously".
     * The returned observable may never emit any results, as subsequent calls to this method will discard in-flight searches.
     * This is intended for use where the dataViewChange event emitter is subscribed to to retrieve the results independent of the search initiation.
     */
    search(): Observable<any> {
        // Stop a running search and restart
        if (this.lastSearchSubscription) {
            this.lastSearchSubscription.unsubscribe();
        }

        // Start the search
        const s: Subject<any> = this.createSearchResultHandler();
        this.lastSearchSubscription = this.createSearch().subscribe(s);
        return s;
    }

    /**
     * Performs the search and returns the results via a promise.
     * The promise will always resolve. Concurrent requests will resolve separately.
     */
    retrieve(timeoutMs?: number): Promise<any> {
        const s: Subject<any> = this.createSearchResultHandler();
        this.createSearch().pipe(
            timeoutMs ? timeout(timeoutMs) : identity,
            take(1)
        ).subscribe(s);
        return firstValueFrom(s);
    }

    get filter(): any {
        return this._internalFilter;
    }

    set filter(value: any) {
        this._internalFilter = value;
    }

    getFilterValue(filterName): any {
        return this._internalFilter[filterName];
    }

    public canSearch(): Promise<boolean> {
        return this.service.canSearch();
    }

    /**
     * Set a filter value triggering FilterValueChangedEvent and FilterChange
     * @param filterName
     * @param value
     */
    setFilterValue(filterName: string, value: any): void {
        if (!this.deepEqual(this.filter[filterName], value)) {
            this.filter[filterName] = value;

            this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, value));
            this.filterChange.emit(this.filter);
        }
    }

    /**
     * Compare two values considering arrays equal when all elements and index are the equal
     * @param value1
     * @param value2
     * @returns {boolean}
     */
    deepEqual(value1: any, value2: any): boolean {
        if (Array.isArray(value1) && Array.isArray(value2)) {
            return value1.length !== value2.length ? false : value1.find((i, index) => i !== value2[index]) === undefined;
        } else {
            return value1 === value2;
        }
    }

    /**
     * Set filter values using a object iterating through the properties and its values triggering FilterValueChangedEvent and FilterChange
     * @param filter
     */
    setFilterValues(filter: any): void {
        let changed: boolean = false;
        for (var filterName in filter) {
            if (filter.hasOwnProperty(filterName)) {
                const value: any = filter[filterName];
                if (this.filter[filterName] !== value) {
                    this.filter[filterName] = value;
                    this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, value));
                    changed = true;
                }
            }
        }
        if (changed) {
            this.filterChange.emit(this.filter);
        }
    }

    /**
     * Returns if the model is dealing with a localisable entity.
     * When dataType is provided it checks if the type is an LocalisableEntityData to determine if the entity is localisable, otherwise defaults to true
     * @returns {boolean}
     */
    isEntityLocalisable(): boolean {
        if (this.dataType) {
            return Object.getPrototypeOf(this.dataType) === LocalisableEntityData;
        } else {
            return true;
        }
    }

    /**
     * Returns the version of the resource.
     * @returns {number}
     */
    getResourceVersion(): number {
        return 0;
    }
}
